"use client";

import { ReactNode } from "react";
import { Link } from "../link";
import { Image } from "../media/image";
import { MoveRight } from "lucide-react"; // Assuming you use lucide-react or similar for icons

type BigCardProperties = {
  url?: string;
  image: {
    url: string;
    alt: string;
  };
  title: string;
  description?: string;
  ctaText?: string;
  [key: string]: unknown;
};

const isVideoSource = (src: string): boolean => {
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
  const lowerSrc = src.toLowerCase();
  return videoExtensions.some((ext) => lowerSrc.endsWith(ext));
};

export function BigCard({
  url,
  image,
  title,
  description,
  ctaText = "Lees meer",
  ...properties
}: BigCardProperties) {

  const isVideo = isVideoSource(image.url);


  const cardContent: ReactNode = (
    <div className="group duration-base relative mx-auto !aspect-[390/466] aspect-[1] overflow-hidden rounded-2xl object-cover shadow-none transition-all hover:shadow-2xl sm:!aspect-[1200/514]">
      {isVideo ? (
        <video
          src={image.url}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-110"
          poster={image.url}
          aria-label={image.alt}
        />
      ) : (
        <Image
          src={image.url}
          alt={image.alt}
          fill
          loading="lazy"
          className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          sizes="(max-width: 640px) 94vw, (max-width: 768px) 90vw, 80vw"
          draggable={false}
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-boldness to-transparent transition-opacity duration-300" />

      <div className="absolute inset-x-0 bottom-0 z-20 p-6 sm:p-10">
        <div className="translate-y-0 transition-transform duration-500 ease-out sm:translate-y-4 sm:group-hover:translate-y-0">

          <h3 className="mb-2 font-heading text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            {title}
          </h3>

          {description && (
            <p className="line-clamp-2 max-w-2xl text-base text-white/80 opacity-100 transition-all duration-500 delay-75 sm:text-lg">
              {description}
            </p>
          )}

          {url && (
            <div className="mt-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white opacity-100 transition-all duration-500 delay-150">
              <span>{ctaText}</span>
              <MoveRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" />
            </div>
          )}
        </div>
      </div>

      {typeof properties.tag === "string" && (
        <div className="absolute top-4 right-4 rounded-full bg-white/10 px-4 py-1 text-xs font-bold text-white backdrop-blur-md">
          {properties.tag}
        </div>
      )}
    </div>
  );

  if (url) {
    return (
      <Link href={url} {...properties} className="block cursor-pointer" draggable={false} prefetch={true}>
        {cardContent}
      </Link>
    );
  }

  return (
    <div {...properties} className="block">
      {cardContent}
    </div>
  );
}